import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Badge, Box, Callout, Card, Flex, Spinner, Tabs, Text } from '@radix-ui/themes'
import { useTRPC } from '~/utils/trpc'
import { CopyButton } from '~/components/CopyButton'
import { isRetired } from '~/features/installations/service'
import {
  ORG_TABS,
  isInstallationView,
  isOrgTab,
  orgTabLabel,
  type InstallationView,
  type OrgTab
} from '~/features/organizations/constants'
import { OrgHeader, type OrgSummaryCounts } from '~/features/organizations/components/OrgHeader'
import { OrgOverviewTab } from '~/features/organizations/components/OrgOverviewTab'
import { OrgInstallationsTab } from '~/features/organizations/components/OrgInstallationsTab'
import { OrgStationsTab } from '~/features/organizations/components/OrgStationsTab'
import { OrgContactsTab } from '~/features/organizations/components/OrgContactsTab'

interface OrganizationsSearch {
  orgId?: string
  tab?: OrgTab
  view?: InstallationView
  stalledOnly?: boolean
}

const DIRECTORY_STALE_MS = 1000 * 60 * 5

const OrganizationsPage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const search = Route.useSearch()

  const orgId = search.orgId
  const tab = search.tab ?? 'overview'
  const view = search.view ?? 'status'
  const stalledOnly = search.stalledOnly ?? false

  // Every org, not just those with open installations: this page is now the destination for
  // every org link in the app, and a fully-installed org must still resolve to a name.
  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions(undefined, { staleTime: DIRECTORY_STALE_MS }))
  const orgs = orgsQuery.data ?? []

  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions(orgId ? { orgId, includeRetired: true } : skipToken)
  )
  const stationsQuery = useQuery(trpc.ops.stations.list.queryOptions(orgId ? { orgId } : skipToken))
  const contactsQuery = useQuery(trpc.ops.contacts.list.queryOptions(orgId ? { orgId } : skipToken))
  const ticketCountsQuery = useQuery(
    trpc.ops.freshdesk.openCounts.queryOptions(undefined, { staleTime: DIRECTORY_STALE_MS })
  )

  // Split once, so "active" is defined in a single place: every count, the Overview tab and the
  // three analytical views read the active side, and only the Retired view reads the other.
  const allInstallations = useMemo(() => installationsQuery.data ?? [], [installationsQuery.data])
  const installations = useMemo(() => allInstallations.filter((row) => !isRetired(row)), [allInstallations])
  const retired = useMemo(() => allInstallations.filter(isRetired), [allInstallations])
  const stations = stationsQuery.data?.stations ?? []
  // The station list is capped server-side; count from the unclipped total so the header
  // and tab badge don't quietly report the cap as the truth.
  const stationTotal = stationsQuery.data?.total ?? stations.length
  const contacts = contactsQuery.data ?? []
  const openTicketCounts = ticketCountsQuery.data?.byInstallationId ?? {}

  // Freshdesk returns every open ticket across all orgs; narrow to the ones whose
  // installation belongs to this org. Null keeps the card hidden when Freshdesk is off.
  const orgTickets = useMemo(() => {
    const data = ticketCountsQuery.data
    if (!data?.configured) return null
    const ids = new Set(installations.map((row) => String(row.installation_id)))
    return data.tickets.filter((ticket) => ids.has(ticket.installationId))
  }, [ticketCountsQuery.data, installations])

  const stalled = useMemo(() => installations.filter((row) => row.current_stall !== null), [installations])

  // Memoised because the tabs key their own derivations off this array's identity; a fresh
  // array every render would defeat those and recompute the timing pipeline on each keystroke.
  const visibleInstallations = useMemo(
    () =>
      stalledOnly
        ? [...stalled].sort((a, b) =>
            (b.current_stall?.occurred_at ?? '').localeCompare(a.current_stall?.occurred_at ?? '')
          )
        : installations,
    [installations, stalled, stalledOnly]
  )

  const setSearch = (patch: Partial<OrganizationsSearch>) => {
    navigate({
      to: '/organizations',
      search: (prev: OrganizationsSearch) => ({ ...prev, ...patch }),
      replace: true
    })
  }

  const goToInstallation = (installationId: number) => {
    navigate({ to: '/installations/$installationId', params: { installationId: String(installationId) } })
  }

  const counts: OrgSummaryCounts | null = orgId
    ? {
        installations: installations.length,
        stalled: stalled.length,
        openTickets: orgTickets?.length ?? 0,
        stations: stationsQuery.isLoading ? null : stationTotal,
        contacts: contactsQuery.isLoading ? null : contacts.length
      }
    : null

  // Only the installations query gates the page — Overview and Installations are built from
  // it. A stations or contacts failure is reported inside its own tab.
  const loadError = installationsQuery.error?.message ?? null

  return (
    <Flex direction="column" gap="4">
      <OrgHeader
        orgs={orgs}
        orgId={orgId}
        loading={orgsQuery.isLoading}
        counts={counts}
        onOrgChange={(nextOrgId) => setSearch({ orgId: nextOrgId, stalledOnly: undefined })}
        onStalledClick={() => setSearch({ tab: 'installations', stalledOnly: true })}
        actions={
          orgId ? (
            <CopyButton value={orgId} label="Copy organization ID">
              Copy org ID
            </CopyButton>
          ) : null
        }
      />

      {orgsQuery.error && <Text color="red">{orgsQuery.error.message}</Text>}

      {!orgId ? (
        <Card size="3">
          <Flex direction="column" gap="2" align="center" py="6">
            <Text color="gray">Pick an organization to see its installations, stations and contacts.</Text>
            <Text size="1" color="gray">
              Press ⌘K to search organizations and installations.
            </Text>
          </Flex>
        </Card>
      ) : installationsQuery.isLoading ? (
        <Flex align="center" gap="2">
          <Spinner size="2" />
          <Text color="gray">Loading…</Text>
        </Flex>
      ) : loadError ? (
        <Callout.Root color="red">
          <Callout.Text>{loadError}</Callout.Text>
        </Callout.Root>
      ) : (
        <Tabs.Root value={tab} onValueChange={(next) => setSearch({ tab: next as OrgTab })}>
          <Tabs.List>
            {ORG_TABS.map((option) => (
              <Tabs.Trigger key={option} value={option}>
                <Flex align="center" gap="2">
                  {orgTabLabel[option]}
                  {counts && option !== 'overview' && counts[option] !== null && (
                    <Badge color="gray" variant="soft" size="1">
                      {counts[option]}
                    </Badge>
                  )}
                </Flex>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Box pt="4">
            <Tabs.Content value="overview">
              <OrgOverviewTab
                installations={installations}
                stalled={stalled}
                tickets={orgTickets}
                onRowClick={goToInstallation}
              />
            </Tabs.Content>

            <Tabs.Content value="installations">
              <OrgInstallationsTab
                installations={visibleInstallations}
                retired={retired}
                view={view}
                stalledOnly={stalledOnly}
                stalledCount={stalled.length}
                openTicketCounts={openTicketCounts}
                onViewChange={(next) => setSearch({ view: next })}
                onStalledOnlyChange={(next) => setSearch({ stalledOnly: next || undefined })}
                onRowClick={goToInstallation}
              />
            </Tabs.Content>

            <Tabs.Content value="stations">
              <OrgStationsTab
                stations={stations}
                total={stationTotal}
                loading={stationsQuery.isLoading}
                error={stationsQuery.error?.message ?? null}
                onRowClick={(stationId) => navigate({ to: '/stations/$stationId', params: { stationId } })}
              />
            </Tabs.Content>

            <Tabs.Content value="contacts">
              <OrgContactsTab
                contacts={contacts}
                loading={contactsQuery.isLoading}
                error={contactsQuery.error?.message ?? null}
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/organizations/')({
  component: OrganizationsPage,
  validateSearch: (raw: Record<string, unknown>): OrganizationsSearch => ({
    orgId: typeof raw.orgId === 'string' && raw.orgId.length > 0 ? raw.orgId : undefined,
    tab: isOrgTab(raw.tab) ? raw.tab : undefined,
    view: isInstallationView(raw.view) ? raw.view : undefined,
    stalledOnly: raw.stalledOnly === true || raw.stalledOnly === 'true' ? true : undefined
  })
})
